import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  motion,
  AnimatePresence,
  useDragControls,
  type PanInfo,
} from 'framer-motion';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/utils/cn';
import { useAuth } from '@/features/authentication/contexts/AuthContext';
import { useSubscriptionStatus } from '@/features/library/hooks/useSubscriptionStatus';
import FeatureGuideOverlay from '@/components/ui/FeatureGuideOverlay';
import ReadingSettingsMenu from '../components/ReadingSettingsMenu';
import MemoizedCardStack from '../components/MemoizedCardStack';
import NovelCharacterChat from '../components/NovelCharacterChat';
import { useCompleteSection } from '../hooks/useCompleteSection';
import { getMe } from '@/services/authService';
import ParagraphInteractionMenu from '../components/ParagraphInteractionMenu';
import CommentsBottomSheet from '../components/CommentsBottomSheet';
import {
  useParagraphInteractionsQuery,
  usePostParagraphInteraction,
} from '../hooks/useParagraphInteractions';
import TimelineBottomSheet from '@/features/learning/components/TimelineBottomSheet';
import CharacterAnalysisBottomSheet from '@/features/learning/components/CharacterAnalysisBottomSheet';
import { useTimelineEvents } from '@/features/learning/hooks/useTimelineEvents';
import { useCharacterAnalysis } from '@/features/learning/hooks/useCharacterAnalysis';
import { useCharacterRelationships } from '@/features/learning/hooks/useCharacterRelationships';
import { History, Users } from 'lucide-react';
import { markOnboardingSectionCompleted } from '@/utils/onboardingProgress';
import { analyticsService } from '@/services/analyticsService';
import { getNotificationAttribution } from '@/services/notificationAttribution';
import {
  getNovelFeatureSpoil,
  setNovelFeatureSpoil,
} from '@/utils/novelFeatureSpoil';

interface NovelReadingState {
  bookId: number;
  chapterId: number;
  sectionId: number;
  sectionTitle: string;
  bookTitle?: string;
  previous_section_summary: string | null;
  trailer: string | null;
  isBookAnalysis?: boolean;
  analysis_text?: string | null;
  returnTo?: string;
  content: {
    paragraphs: { index: number; content: string; reactions: any[] | number }[];
  } | null;
  reviewMode?: 'chapter_review_novel';
  reviewSectionIndex?: number;
  reviewSections?: {
    sectionId: number;
    sectionTitle: string;
    paragraphs: { index: number; content: string; reactions: any[] | number }[];
  }[];
  onboardingPreview?: boolean;
  source?: string;
  notificationCampaign?: string;
  onboardingFeatureFlags?: {
    hasChatCharacters: boolean;
    hasCharacterInsights: boolean;
    hasTimeline: boolean;
  };
}

type OnboardingNovelStep =
  | 'paragraph'
  | 'chat'
  | 'characters'
  | 'timeline'
  | 'trailer';

// Survive a remount of the same route entry. Otherwise the state initializer
// would replay the intro splash even though the user never opened a new part.
const initialNovelSplashShown = new Set<string>();

const SPLASH_DOT_DELAYS = [0.35, 0.65, 0.95] as const;
const SPLASH_DOT_DURATION = 0.2;
const SPLASH_READING_DELAY_MS = 350;
const SPLASH_READING_MS = 1150 + SPLASH_READING_DELAY_MS;

const SplashOverlay = ({
  text,
  onComplete,
}: {
  text: string;
  onComplete: () => void;
}) => {
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    const timer = setTimeout(() => {
      onCompleteRef.current();
    }, SPLASH_READING_MS);
    return () => clearTimeout(timer);
  }, []);

  const sizeClass =
    text.length > 42
      ? 'text-xl sm:text-2xl md:text-3xl'
      : text.length > 26
        ? 'text-2xl sm:text-3xl'
        : 'text-3xl sm:text-4xl';

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6, ease: 'easeInOut' }}
      className="absolute inset-0 z-[100] flex items-center justify-center backdrop-blur-xl bg-black/60 px-4 py-10"
      style={{ touchAction: 'none' }}
    >
      <div
        className={cn(
          'text-white font-bold font-persian drop-shadow-2xl text-center leading-normal break-words max-w-[92vw]',
          sizeClass
        )}
        dir="rtl"
      >
        <motion.span
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {text}
          <span className="inline-flex mr-1 font-sans text-4xl align-middle translate-y-[-4px]">
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{
                delay: SPLASH_DOT_DELAYS[0],
                duration: SPLASH_DOT_DURATION,
              }}
            >
              .
            </motion.span>
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{
                delay: SPLASH_DOT_DELAYS[1],
                duration: SPLASH_DOT_DURATION,
              }}
            >
              .
            </motion.span>
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{
                delay: SPLASH_DOT_DELAYS[2],
                duration: SPLASH_DOT_DURATION,
              }}
            >
              .
            </motion.span>
          </span>
        </motion.span>
      </div>
    </motion.div>
  );
};

type CardType = 'previous_summary' | 'paragraph' | 'trailer';

interface NovelCard {
  type: CardType;
  id: string;
  content: string;
  paragraphIndex?: number;
  sectionId?: number;
  sectionTitle?: string;
  reactions?:
    | { reaction: string; count: number; percentage: number }[]
    | number;
}

const NOVEL_ONBOARDING_GUIDE = {
  firstParagraph: 'اینجا جای تعامل با قصه هاست',
  tapDone: 'با شخصیت ها حرف بزن !؟',
  chatAnswered: 'چه طور بود ؟ ادامه بده',
  needGems: 'الماس نداری ؟ صفحه رو ببند تا بعد از تکمیل این بخش بهت الماس بدم',
  generalError: 'نمیدونم چی شده.مهم نیست صفحه رو ببند تا ادامه بدیم',
  characterIntro: 'تحلیل و روابط شخصیت ها رو ببین !',
  timelineIntro: 'اتفاقات داستان رو از دست نده !',
  trailerIntro: 'یعنی قسمت بعدی چی میشه ؟',
} as const;

const NOVEL_GUIDE_STAGE = {
  INIT: 0,
  FIRST_PARAGRAPH_SEEN: 1,
  TAP_DONE: 2,
  CHAT_RESULT: 3,
  CHARACTER_OPENED: 4,
  TRAILER_ENTERED: 5,
} as const;

const NOVEL_READING_FEATURE_GUIDES = {
  readingOverview: [
    {
      id: 'novel-reading-overview',
      title: 'مطالعه داستان',
      description:
        'داستان رو به صورت پاراگراف بندی شده بخون و با ضربه زدن روی پاراگراف از ویژگی های داستان استفاده کن',
    },
  ],
  previousSummary: [
    {
      id: 'novel-reading-previous-summary',
      title: 'آنچه گذشت',
      description: 'اینجا میتونی خلاصه قسمت قبلی رو بخونی',
    },
  ],
  trailer: [
    {
      id: 'novel-reading-trailer',
      title: 'آنچه خواهید خواند',
      description: 'اینجا میتونی یه معرفی کوتاه از قسمت بعدی رو بخونی',
    },
  ],
  interactions: [
    {
      id: 'novel-reading-reaction-menu',
      title: 'ری‌اکشن پاراگراف',
      description: 'احساس و نظرتو به هر پاراگراف میتونی نشون بدی.',
      target: '[data-guide="novel-reading-reaction-menu"]',
    },
    {
      id: 'novel-reading-character-chat-button',
      title: 'چت با شخصیت‌ها',
      description: 'با کلیک روی این دکمه میتونی با شخصیت‌های این کتاب حرف بزنی',
      target: '[data-guide="novel-reading-character-chat-button"]',
    },
    {
      id: 'novel-reading-character-analysis-button',
      title: 'شخصیت‌ها',
      description:
        'با کلیک روی این دکمه میتونی تحلیل و روابط شخصیت‌های این کتاب رو ببینی',
      target: '[data-guide="novel-reading-character-analysis-button"]',
    },
    {
      id: 'novel-reading-timeline-button',
      title: 'خط زمانی داستان',
      description: 'با کلیک روی این دکمه میتونی خط زمانی داستان رو دنبال کنی',
      target: '[data-guide="novel-reading-timeline-button"]',
    },
  ],
};

const REACTION_EMOJI_MAP: Record<string, string> = {
  happy: '😄',
  sad: '😢',
  excited: '🤩',
  angry: '😡',
  surprised: '😲',
  love: '❤️',
  fear: '😨',
  laugh: '😂',
  cry: '😭',
  hot: '🔥',
};

const ParagraphCardReactions = ({
  sectionId,
  paragraphIndex,
  isCurrent,
  initialTotalReactions,
  theme = 'white',
  onReactionToggle,
  toPersianDigits,
}: {
  sectionId: number;
  paragraphIndex: number;
  isCurrent: boolean;
  onReactionToggle: (pIdx: number, key: string) => void;
  toPersianDigits: (n: number | string) => string;
  initialTotalReactions?: number;
  theme?: 'white' | 'sepia' | 'dark';
}) => {
  const { data } = useParagraphInteractionsQuery(sectionId, paragraphIndex);
  const serverReactions = data?.paragraph_reactions || [];
  const userReaction = data?.user_reaction;
  const isPremium = data?.is_premium;

  const stableOrderRef = React.useRef<string[]>([]);
  const [stableOrder, setStableOrder] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (serverReactions.length > 0 && stableOrderRef.current.length === 0) {
      const order = serverReactions.map(r => r.reaction);
      stableOrderRef.current = order;
      setStableOrder(order);
    } else if (serverReactions.length > 0) {
      const incoming = serverReactions.map(r => r.reaction);
      const newKeys = incoming.filter(k => !stableOrderRef.current.includes(k));
      if (newKeys.length > 0) {
        const updated = [...stableOrderRef.current, ...newKeys];
        stableOrderRef.current = updated;
        setStableOrder(updated);
      }
    }
  }, [serverReactions]);

  const reactionsMap = React.useMemo(() => {
    const map: Record<string, number> = {};
    serverReactions.forEach(r => {
      map[r.reaction] = r.count;
    });
    return map;
  }, [serverReactions]);

  if (isPremium === false) {
    if (
      typeof initialTotalReactions === 'number' &&
      initialTotalReactions > 0
    ) {
      return (
        <div className="w-full mt-2 mb-0 flex flex-row justify-start flex-shrink-0">
          <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/10 border border-transparent">
            <svg
              width="24"
              height="24"
              viewBox="0 0 512 512"
              className="flex-shrink-0 drop-shadow-sm"
            >
              <circle
                cx="160"
                cy="180"
                r="140"
                fill="#ff5c5c"
                stroke="#000"
                strokeWidth="15"
              />
              <circle
                cx="352"
                cy="180"
                r="140"
                fill="#98eb4d"
                stroke="#000"
                strokeWidth="15"
              />
              <circle
                cx="256"
                cy="340"
                r="140"
                fill="#ffcc00"
                stroke="#000"
                strokeWidth="20"
              />

              <g fill="#000">
                <circle cx="120" cy="150" r="18" />
                <circle cx="200" cy="150" r="18" />
                <circle cx="312" cy="150" r="18" />
                <circle cx="392" cy="150" r="18" />
                <circle cx="216" cy="310" r="18" />
                <circle cx="296" cy="310" r="18" />
                <path
                  d="M190 360 Q256 410 322 360"
                  stroke="#000"
                  strokeWidth="15"
                  fill="none"
                  strokeLinecap="round"
                />
              </g>
            </svg>
            <span
              className={cn(
                'font-persian text-sm font-bold mt-0.5',
                isCurrent || theme === 'dark' || theme === 'sepia'
                  ? 'text-white'
                  : 'text-gray-800'
              )}
            >
              {toPersianDigits(initialTotalReactions)}
            </span>
          </div>
        </div>
      );
    }
    return null;
  }

  if (serverReactions.length === 0) return null;

  return (
    <div className="w-full mt-2 mb-0 flex flex-row flex-wrap gap-1.5 justify-start flex-shrink-0">
      {stableOrder
        .filter(key => (reactionsMap[key] ?? 0) > 0 || userReaction === key)
        .map(key => {
          const count = reactionsMap[key] ?? 0;
          const isSelected = userReaction === key;
          return (
            <button
              key={key}
              className={cn(
                'flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium shadow-sm transition-all active:scale-95 border mb-1',
                isSelected
                  ? 'bg-white/20 border-white scale-105 shadow-md'
                  : 'bg-black/10 hover:bg-black/20 border-transparent'
              )}
              onClick={e => {
                if (isCurrent) {
                  e.stopPropagation();
                  onReactionToggle(paragraphIndex, key);
                }
              }}
            >
              <span className="text-base leading-none">
                {REACTION_EMOJI_MAP[key] || '🙂'}
              </span>
              <span
                className={cn(
                  'font-persian text-[11px] font-bold mt-0.5',
                  isSelected ? 'text-white' : 'opacity-80'
                )}
              >
                {toPersianDigits(count)}
              </span>
            </button>
          );
        })}
    </div>
  );
};

const NovelReadingPage: React.FC = () => {
  const { user } = useAuth();
  const { isSubscriptionActive, isLoading: subscriptionLoading } = useSubscriptionStatus();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const state = location.state as NovelReadingState | null;
  const bookId = state?.bookId;
  const sectionId = state?.sectionId;
  const isBookAnalysis = Boolean(state?.isBookAnalysis);
  const splashEntryKey = `${location.key}:${sectionId ?? 'unknown'}:${isBookAnalysis ? 'analysis' : 'reading'}`;
  const analysisText = state?.analysis_text || '';
  const returnTo = state?.returnTo || '/learning/path';
  const reviewMode = state?.reviewMode;
  const onboardingPreview = state?.onboardingPreview === true;
  const onboardingFeatureFlags = onboardingPreview
    ? state?.onboardingFeatureFlags
    : undefined;
  const hasChatCharacters = onboardingFeatureFlags?.hasChatCharacters === true;
  const hasCharacterInsights =
    onboardingFeatureFlags?.hasCharacterInsights === true;
  const hasTimeline = onboardingFeatureFlags?.hasTimeline === true;
  const onboardingSheetMaxHeightVh = onboardingPreview ? 74 : undefined;
  const onboardingCompact = onboardingPreview;
  const shouldShowOnboardingParagraph =
    !onboardingPreview ||
    (!hasChatCharacters && !hasCharacterInsights && !hasTimeline);
  const reviewSectionIndex = state?.reviewSectionIndex || 0;
  const reviewSections = state?.reviewSections || [];

  const logNovelFeatureClick = (
    feature: 'character_analysis' | 'timeline' | 'character_chat'
  ) => {
    void analyticsService.logEvent('novel_feature_click', {
      feature,
      page_context: 'novel_read',
      flow: onboardingPreview ? 'onboarding' : 'normal',
      book_id: bookId ? String(bookId) : 'unknown',
      book_title: state?.bookTitle || 'unknown',
      ...(sectionId ? { section_id: String(sectionId) } : {}),
    });
  };

  const previousSummary = isBookAnalysis
    ? null
    : state?.previous_section_summary;
  const trailer = isBookAnalysis ? null : state?.trailer;
  const paragraphsRaw = isBookAnalysis ? [] : state?.content?.paragraphs || [];
  const paragraphs = onboardingPreview
    ? shouldShowOnboardingParagraph
      ? paragraphsRaw.slice(0, 1)
      : []
    : paragraphsRaw;

  useEffect(() => {
    if (onboardingPreview && state?.bookId) {
      localStorage.setItem('onboarding_book_id', String(state.bookId));
    }
  }, [onboardingPreview, state?.bookId]);

  const error = !state ? 'داده‌های بخش یافت نشد' : null;

  const completeSectionMutation = useCompleteSection();

  const [uiVisible, setUiVisible] = useState<boolean>(false);
  const [pageBgColor, setPageBgColor] = useState<'white' | 'sepia' | 'dark'>(
    'white'
  );
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [hasOpenedChat, setHasOpenedChat] = useState(false);
  const [lastTapOnCard, setLastTapOnCard] = useState(false);
  const [isTimelineOpen, setIsTimelineOpen] = useState(false);
  const [isCharacterAnalysisOpen, setIsCharacterAnalysisOpen] = useState(false);
  const [isCommentsSheetOpen, setIsCommentsSheetOpen] = useState(false);
  const [hasOpenedTimeline, setHasOpenedTimeline] = useState(false);
  const [hasOpenedCharacterAnalysis, setHasOpenedCharacterAnalysis] =
    useState(false);
  const [hasOpenedComments, setHasOpenedComments] = useState(false);
  const [isTimelineSpoil, setIsTimelineSpoil] = useState(() =>
    onboardingPreview || getNovelFeatureSpoil(bookId, 'timeline')
  );
  const [isCharacterAnalysisSpoil, setIsCharacterAnalysisSpoil] = useState(() =>
    onboardingPreview || getNovelFeatureSpoil(bookId, 'character-analysis')
  );
  const [isCharacterRelationshipsSpoil, setIsCharacterRelationshipsSpoil] =
    useState(() => onboardingPreview || getNovelFeatureSpoil(bookId, 'character-relationships'));
  const [onboardingNovelStep, setOnboardingNovelStep] =
    useState<OnboardingNovelStep>('paragraph');
  const [onboardingReactionMenuOpen, setOnboardingReactionMenuOpen] =
    useState(true);
  const [onboardingReactionGiven, setOnboardingReactionGiven] = useState(false);
  const sectionStartTrackedRef = useRef<number | null>(null);

  useEffect(() => {
    setIsTimelineSpoil(onboardingPreview || getNovelFeatureSpoil(bookId, 'timeline'));
    setIsCharacterAnalysisSpoil(
      onboardingPreview || getNovelFeatureSpoil(bookId, 'character-analysis')
    );
    setIsCharacterRelationshipsSpoil(
      onboardingPreview || getNovelFeatureSpoil(bookId, 'character-relationships')
    );
  }, [bookId, onboardingPreview]);

  const handleTimelineSpoilChange = useCallback(
    (value: boolean) => {
      setIsTimelineSpoil(value);
      if (!onboardingPreview) setNovelFeatureSpoil(bookId, 'timeline', value);
    },
    [bookId, onboardingPreview]
  );

  const handleCharacterAnalysisSpoilChange = useCallback(
    (value: boolean) => {
      setIsCharacterAnalysisSpoil(value);
      if (!onboardingPreview) setNovelFeatureSpoil(bookId, 'character-analysis', value);
    },
    [bookId, onboardingPreview]
  );

  const handleCharacterRelationshipsSpoilChange = useCallback(
    (value: boolean) => {
      setIsCharacterRelationshipsSpoil(value);
      if (!onboardingPreview) setNovelFeatureSpoil(bookId, 'character-relationships', value);
    },
    [bookId, onboardingPreview]
  );

  useEffect(() => {
    if (
      !sectionId ||
      isBookAnalysis ||
      sectionStartTrackedRef.current === sectionId
    )
      return;
    sectionStartTrackedRef.current = sectionId;

    const notificationAttribution = getNotificationAttribution();
    const hasMatchingNotificationAttribution =
      notificationAttribution &&
      (!notificationAttribution.targetBookId ||
        notificationAttribution.targetBookId === Number(bookId));

    void analyticsService.logEvent('section_started', {
      section_id: String(sectionId),
      book_id: bookId ? String(bookId) : 'unknown',
      book_title: state?.bookTitle || 'unknown',
      content_type: 'novel',
      page_context: 'novel_read',
      flow: onboardingPreview ? 'onboarding' : 'normal',
      ...(state?.source === 'notification' || hasMatchingNotificationAttribution
        ? {
            source: 'notification',
            notification_campaign:
              state?.notificationCampaign ||
              notificationAttribution?.campaign ||
              'unknown',
          }
        : {}),
    });
  }, [
    bookId,
    isBookAnalysis,
    sectionId,
    state?.bookTitle,
    state?.notificationCampaign,
    state?.source,
  ]);
  const [chatHasAnswered, setChatHasAnswered] = useState(false);
  const [guideStage, setGuideStage] = useState<number>(NOVEL_GUIDE_STAGE.INIT);
  const guideStageRef = useRef<number>(NOVEL_GUIDE_STAGE.INIT);
  const guideTextRef = useRef('');
  const [highlightChatButton, setHighlightChatButton] = useState(false);
  const [highlightCharacterButton, setHighlightCharacterButton] =
    useState(false);
  const guideSequenceTokenRef = useRef(0);
  const characterHighlightConsumedRef = useRef(false);

  const initialSplashState = onboardingPreview
    ? 'intro'
    : previousSummary && previousSummary.trim() !== ''
      ? 'previous'
      : 'intro';
  const [splashState, setSplashState] = useState<
    'intro' | 'previous' | 'trailer' | 'none'
  >(() => {
    if (initialNovelSplashShown.has(splashEntryKey)) return 'none';
    return initialSplashState;
  });
  const [splashTitle, setSplashTitle] = useState<string>(
    state?.sectionTitle || 'شروع بخش'
  );
  const [showPreviousSummaryGuide, setShowPreviousSummaryGuide] =
    useState(false);
  const [showTrailerGuide, setShowTrailerGuide] = useState(false);
  const [showReadingOverviewGuide, setShowReadingOverviewGuide] =
    useState(false);
  const featureGuideDelayRef = useRef<number | null>(null);
  const splashContextKeyRef = useRef<string | null>(null);
  const shownSectionSplashRef = useRef<Set<number>>(new Set());

  const trailerSplashShown = useRef(false);
  const introSplashShown = useRef(
    !previousSummary || previousSummary.trim() === ''
  );

  const scheduleFeatureGuide = useCallback((showGuide: () => void) => {
    if (featureGuideDelayRef.current !== null) {
      window.clearTimeout(featureGuideDelayRef.current);
    }
    featureGuideDelayRef.current = window.setTimeout(showGuide, 650);
  }, []);

  const handlePreviousSplashComplete = useCallback(() => {
    setSplashState('none');
    if (!onboardingPreview && !isBookAnalysis && previousSummary?.trim()) {
      scheduleFeatureGuide(() => setShowPreviousSummaryGuide(true));
    }
  }, [
    isBookAnalysis,
    onboardingPreview,
    previousSummary,
    scheduleFeatureGuide,
  ]);

  const handleIntroSplashComplete = useCallback(() => {
    setSplashState('none');
    if (!onboardingPreview && !isBookAnalysis && paragraphs.length > 0) {
      scheduleFeatureGuide(() => setShowReadingOverviewGuide(true));
    }
  }, [
    isBookAnalysis,
    onboardingPreview,
    paragraphs.length,
    scheduleFeatureGuide,
  ]);

  const handleTrailerSplashComplete = useCallback(() => {
    setSplashState('none');
    if (!onboardingPreview && !isBookAnalysis && trailer?.trim()) {
      scheduleFeatureGuide(() => setShowTrailerGuide(true));
    }
  }, [isBookAnalysis, onboardingPreview, scheduleFeatureGuide, trailer]);

  useEffect(
    () => () => {
      if (featureGuideDelayRef.current !== null) {
        window.clearTimeout(featureGuideDelayRef.current);
      }
    },
    []
  );

  const [currentCardIndex, setCurrentCardIndex] = useState<number>(0);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const [sessionStartTime] = useState(Date.now());
  const [initialGems, setInitialGems] = useState<number>(0);

  const [windowHeight, setWindowHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 800
  );
  const [onboardingCardHeight, setOnboardingCardHeight] = useState(0);

  useEffect(() => {
    if (chatOpen) setHasOpenedChat(true);
    if (isTimelineOpen) setHasOpenedTimeline(true);
    if (isCharacterAnalysisOpen) setHasOpenedCharacterAnalysis(true);
    if (isCommentsSheetOpen) setHasOpenedComments(true);
  }, [chatOpen, isTimelineOpen, isCharacterAnalysisOpen, isCommentsSheetOpen]);

  React.useLayoutEffect(() => {
    const splashContextKey = JSON.stringify([
      onboardingPreview,
      isBookAnalysis,
      sectionId,
    ]);
    if (splashContextKeyRef.current === splashContextKey) return;
    if (splashContextKeyRef.current === null) {
      splashContextKeyRef.current = splashContextKey;
      initialNovelSplashShown.add(splashEntryKey);
      return;
    }
    splashContextKeyRef.current = splashContextKey;

    const nextSplash = initialNovelSplashShown.has(splashEntryKey)
      ? 'none'
      : onboardingPreview
        ? 'intro'
        : previousSummary && previousSummary.trim() !== ''
          ? 'previous'
          : 'intro';
    initialNovelSplashShown.add(splashEntryKey);
    setSplashState(nextSplash);
    setSplashTitle(state?.sectionTitle || 'شروع بخش');
    trailerSplashShown.current = false;
    introSplashShown.current =
      !previousSummary || previousSummary.trim() === '';
    setCurrentCardIndex(0);
    setUiVisible(false);
    setShowPreviousSummaryGuide(false);
    setShowTrailerGuide(false);
    setShowReadingOverviewGuide(false);
    if (featureGuideDelayRef.current !== null) {
      window.clearTimeout(featureGuideDelayRef.current);
      featureGuideDelayRef.current = null;
    }
    shownSectionSplashRef.current = new Set(sectionId ? [sectionId] : []);
  }, [onboardingPreview, isBookAnalysis, sectionId, splashEntryKey]);

  useEffect(() => {
    const handleResize = () => setWindowHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!onboardingPreview) {
      setOnboardingCardHeight(0);
      return;
    }

    const readCardHeight = () => {
      const card = document.querySelector<HTMLElement>('.current-novel-card');
      if (card) setOnboardingCardHeight(card.getBoundingClientRect().height);
    };

    readCardHeight();
    const card = document.querySelector<HTMLElement>('.current-novel-card');
    let observer: ResizeObserver | undefined;
    if (card && 'ResizeObserver' in window) {
      observer = new ResizeObserver(readCardHeight);
      observer.observe(card);
    }
    window.addEventListener('resize', readCardHeight);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', readCardHeight);
    };
  }, [currentCardIndex, onboardingPreview, splashState]);

  useEffect(() => {
    (async () => {
      try {
        const profile = await getMe();
        if (profile?.gems != null) setInitialGems(profile.gems);
      } catch (e) {}
    })();
  }, []);

  const {
    data: timelineData,
    isLoading: timelineLoading,
    isError: timelineError,
    error: timelineErrorObj,
    refetch: refetchTimeline,
  } = useTimelineEvents(bookId || 0, {
    enabled: !!bookId && (!onboardingPreview || hasTimeline),
    isSpoil: isTimelineSpoil,
  });

  const {
    data: characterAnalysisData,
    isLoading: characterAnalysisLoading,
    isError: characterAnalysisError,
    error: characterAnalysisErrorObj,
    refetch: refetchCharacterAnalysis,
  } = useCharacterAnalysis(bookId || 0, {
    enabled: !!bookId && (!onboardingPreview || hasCharacterInsights),
    isSpoil: isCharacterAnalysisSpoil,
  });

  const {
    data: characterRelationshipsData,
    isLoading: characterRelationshipsLoading,
    isError: characterRelationshipsError,
    error: characterRelationshipsErrorObj,
    refetch: refetchCharacterRelationships,
  } = useCharacterRelationships(bookId || 0, {
    enabled: !!bookId && (!onboardingPreview || hasCharacterInsights),
    isSpoil: isCharacterRelationshipsSpoil,
  });

  const effectiveTimelineData = timelineData;
  const effectiveCharacterAnalysisData = characterAnalysisData;
  const effectiveCharacterRelationshipsData = characterRelationshipsData;
  const canDisableNovelSpoil = onboardingPreview
    ? true
    : subscriptionLoading || !timelineData || !characterAnalysisData || !characterRelationshipsData
      ? undefined
      : isSubscriptionActive || timelineData.is_premium === true
        || characterAnalysisData.is_premium === true
        || characterRelationshipsData.is_premium === true;

  useEffect(() => {
    if (onboardingPreview || subscriptionLoading || !timelineData || !characterAnalysisData || !characterRelationshipsData || canDisableNovelSpoil !== false) return;
    setIsTimelineSpoil(false);
    setIsCharacterAnalysisSpoil(false);
    setIsCharacterRelationshipsSpoil(false);
    setNovelFeatureSpoil(bookId, 'timeline', false);
    setNovelFeatureSpoil(bookId, 'character-analysis', false);
    setNovelFeatureSpoil(bookId, 'character-relationships', false);
  }, [bookId, canDisableNovelSpoil, onboardingPreview, subscriptionLoading, timelineData, characterAnalysisData, characterRelationshipsData]);
  const timelineSections = effectiveTimelineData?.data?.sections ?? [];
  const mergedCharacters = useMemo(() => {
    const analysisList = effectiveCharacterAnalysisData?.data?.characters ?? [];
    const relationshipsList =
      effectiveCharacterRelationshipsData?.data?.characters ?? [];

    return analysisList.map(char => {
      const relationshipData = relationshipsList.find(
        r => r.character_name === char.character_name
      );
      return {
        ...char,
        relationships: relationshipData?.relationships ?? [],
        character_id: relationshipData?.character_id,
      };
    });
  }, [effectiveCharacterAnalysisData, effectiveCharacterRelationshipsData]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'none';
    return () => {
      document.body.style.overflow = '';
      document.body.style.overscrollBehavior = '';
    };
  }, []);

  const goBack = useCallback(() => {
    if (onboardingPreview) {
      navigate('/onboarding/books', {
        replace: true,
        state: {
          bookId: state?.bookId,
          disableBookSwitcher: true,
          fromOnboarding: true,
        },
      });
      return;
    }
    navigate(-1);
  }, [navigate, onboardingPreview, state?.bookId]);

  const setOnboardingGuide = useCallback(
    (text: string, replay = false) => {
      if (!onboardingPreview) return;
      if (!replay && guideTextRef.current === text) return;
      guideTextRef.current = text;
      window.dispatchEvent(
        new CustomEvent('onboarding-read-guide', { detail: { text, replay } })
      );
    },
    [onboardingPreview]
  );

  const setGuideWithStage = useCallback(
    (stage: number, text: string, replay = false) => {
      if (!onboardingPreview) return;
      if (stage < guideStageRef.current) return;
      if (stage > guideStageRef.current) {
        // Any newer stage should immediately cancel older in-flight guide sequences.
        guideSequenceTokenRef.current += 1;
        guideStageRef.current = stage;
        setGuideStage(stage);
      }
      setOnboardingGuide(text, replay);
    },
    [onboardingPreview, setOnboardingGuide]
  );

  const toPersianDigits = useCallback((num: number | string): string => {
    const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    return String(num).replace(/[0-9]/g, d => persianDigits[parseInt(d, 10)]);
  }, []);

  const getCharLimit = useCallback((): number => {
    if (onboardingPreview) {
      const headerHeight =
        typeof window !== 'undefined'
          ? parseFloat(
              getComputedStyle(document.documentElement).getPropertyValue(
                '--onboarding-header-h'
              )
            ) || 110
          : 110;
      const cardHeight =
        onboardingCardHeight > 0
          ? onboardingCardHeight
          : Math.max(220, windowHeight - headerHeight - 132);
      const fontPx = Math.max(
        14,
        Math.min(19, Math.min(window.innerWidth * 0.045, windowHeight * 0.024))
      );
      const lineHeight = fontPx * 2.05;
      const contentHeight = Math.max(120, cardHeight - 74);
      const linesPerCard = Math.max(6, Math.floor(contentHeight / lineHeight));
      const cardWidth = Math.min(window.innerWidth * 0.9, 390) - 32;
      const charsPerLine = Math.max(
        24,
        Math.floor(cardWidth / (fontPx * 0.42))
      );
      return Math.floor(linesPerCard * charsPerLine * 0.96);
    }

    const fontPx = 17;
    const lineHeight = fontPx * 2;
    const linesPerCard = Math.max(
      1,
      Math.floor((windowHeight * 0.55) / lineHeight)
    );
    const charsPerLine = Math.max(30, Math.floor(320 / (fontPx * 0.45)));
    return Math.floor(linesPerCard * charsPerLine);
  }, [onboardingCardHeight, windowHeight, onboardingPreview]);

  const getOnboardingNovelFontSize = useCallback(
    (content = '', reservedHeight = 0, isTrailer = false) => {
      if (!onboardingPreview) return 17;
      const headerHeight =
        typeof window !== 'undefined'
          ? parseFloat(
              getComputedStyle(document.documentElement).getPropertyValue(
                '--onboarding-header-h'
              )
            ) || 110
          : 110;
      // The preview determines the wrap-content card height, so it must be
      // calculated from the available maximum space, not the current card
      // measurement from a previous render.
      const cardHeight = Math.max(220, windowHeight - headerHeight - 132);
      const cardOuterWidth =
        window.innerWidth >= 640
          ? Math.min(window.innerWidth * 0.86, 390)
          : Math.min(window.innerWidth * 0.9, 340);
      const cardWidth = cardOuterWidth - (window.innerWidth >= 640 ? 24 : 20);
      const trailerHeaderReserve = isTrailer ? 64 : 0;
      const availableHeight = Math.max(
        96,
        (cardHeight - 32 - reservedHeight - trailerHeaderReserve) * 0.98
      );
      const maxDeviceFont = Math.min(
        19.5,
        window.innerWidth * 0.052,
        windowHeight * 0.026
      );

      for (let fontPx = maxDeviceFont; fontPx >= 9; fontPx -= 0.25) {
        const charsPerLine = Math.max(
          14,
          Math.floor(cardWidth / (fontPx * 0.56))
        );
        const lineCount = content
          ? content
              .split(/\n/)
              .reduce(
                (total, line) =>
                  total +
                  Math.max(1, Math.ceil(line.trim().length / charsPerLine)),
                0
              )
          : 1;
        if (lineCount * fontPx * 2.05 <= availableHeight) {
          const calculatedSize = Number(fontPx.toFixed(2));
          return calculatedSize;
        }
      }

      return 9;
    },
    [onboardingCardHeight, onboardingPreview, windowHeight]
  );

  const getOnboardingTrailerPreview = useCallback(
    (content = '') => {
      if (!content.trim()) return '';

      const headerHeight =
        typeof window !== 'undefined'
          ? parseFloat(
              getComputedStyle(document.documentElement).getPropertyValue(
                '--onboarding-header-h'
              )
            ) || 110
          : 110;
      // The preview determines the wrap-content card height, so calculate it
      // from the maximum available space rather than a previous card measure.
      const cardHeight = Math.max(220, windowHeight - headerHeight - 132);
      const cardOuterWidth =
        typeof window !== 'undefined' && window.innerWidth >= 640
          ? Math.min(window.innerWidth * 0.86, 390)
          : Math.min(
              (typeof window !== 'undefined' ? window.innerWidth : 390) * 0.9,
              340
            );
      const cardWidth =
        cardOuterWidth -
        (typeof window !== 'undefined' && window.innerWidth >= 640 ? 24 : 20);
      const fontPx = Math.min(
        15,
        window.innerWidth * 0.042,
        windowHeight * 0.021
      );
      const lineHeight = fontPx * 2.05;
      const charsPerLine = Math.max(
        14,
        Math.floor(cardWidth / (fontPx * 0.56))
      );
      // The trailer card also contains the "آنچه خواهید خواند" header.
      // Reserve its title, spacing, and border before calculating text capacity.
      const trailerHeaderReserve = 64;
      const maxLines = Math.max(
        1,
        Math.floor(
          Math.max(96, cardHeight - 24 - trailerHeaderReserve) / lineHeight
        )
      );
      const maxCharacters = charsPerLine * maxLines;
      const normalizedContent = content.trim();
      const fittingPrefix = normalizedContent.slice(0, maxCharacters);
      const lastParagraphEnd = Math.max(
        fittingPrefix.lastIndexOf('.'),
        fittingPrefix.lastIndexOf(';'),
        fittingPrefix.lastIndexOf('؛')
      );

      if (lastParagraphEnd >= 0)
        return fittingPrefix.slice(0, lastParagraphEnd + 1).trim();

      // If the first fitting area has no delimiter, use the next complete
      // paragraph rather than ending the preview mid-sentence.
      const nextParagraphEnd = normalizedContent.search(/[.;؛](?=\s|$)/);
      return nextParagraphEnd >= 0
        ? normalizedContent.slice(0, nextParagraphEnd + 1).trim()
        : normalizedContent;
    },
    [onboardingCardHeight, windowHeight]
  );

  const getNovelFontSize = useCallback(
    (content = '', hasReactions = false) => {
      if (onboardingPreview || !content.trim()) return 17;

      const viewportWidth =
        typeof window !== 'undefined' ? window.innerWidth : 390;
      const cardWidth = Math.min(viewportWidth * 0.9, 448) - 32;
      const availableHeight = Math.max(
        180,
        windowHeight - 192 - (hasReactions ? 60 : 0)
      );

      // Fit the complete paragraph in the existing card instead of
      // clipping it. The card gesture remains untouched; only the text
      // metrics are adapted for unusually long paragraphs.
      for (let fontPx = 17; fontPx >= 9; fontPx -= 0.25) {
        const charsPerLine = Math.max(
          16,
          Math.floor(cardWidth / (fontPx * 0.52))
        );
        const lineCount = content
          .split(/\n/)
          .reduce(
            (total, line) =>
              total + Math.max(1, Math.ceil(line.trim().length / charsPerLine)),
            0
          );
        if (lineCount * fontPx * 2.2 <= availableHeight) {
          return Number(fontPx.toFixed(2));
        }
      }

      return 9;
    },
    [onboardingPreview, windowHeight]
  );

  const splitTextByLimit = useCallback(
    (content: string, charLimit: number): string[] => {
      if (!content || !content.trim()) return [];
      const normalizedContent = content
        .replace(/\n+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      const words = normalizedContent.split(/\s+/).filter(Boolean);
      const pages: string[] = [];
      let currentPage = '';
      for (const word of words) {
        const separator = currentPage ? ' ' : '';
        const newLength = currentPage.length + separator.length + word.length;
        if (newLength > charLimit && currentPage.trim()) {
          pages.push(currentPage.trim());
          currentPage = word;
        } else {
          currentPage += separator + word;
        }
      }
      if (currentPage.trim()) {
        pages.push(currentPage.trim());
      }
      return pages.length > 0 ? pages : [content];
    },
    []
  );

  const analysisParagraphs = useMemo(() => {
    if (!isBookAnalysis || !analysisText.trim()) return [];
    return splitTextByLimit(analysisText, getCharLimit()).map(
      (content, index) => ({
        index,
        content,
      })
    );
  }, [analysisText, getCharLimit, isBookAnalysis, splitTextByLimit]);

  const unifiedCards = useMemo<NovelCard[]>(() => {
    const cards: NovelCard[] = [];
    const charLimit = getCharLimit();

    if (isBookAnalysis) {
      analysisParagraphs.forEach(paragraph => {
        cards.push({
          type: 'paragraph',
          id: `analysis-${paragraph.index}`,
          content: paragraph.content,
          paragraphIndex: paragraph.index,
        });
      });

      return cards;
    }

    if (
      !onboardingPreview &&
      previousSummary &&
      previousSummary.trim() !== ''
    ) {
      const parts = splitTextByLimit(previousSummary, charLimit);
      parts.forEach((part, idx) => {
        cards.push({
          type: 'previous_summary',
          id: `prev-summary-${idx}`,
          content: part,
        });
      });
    }

    if (reviewMode === 'chapter_review_novel' && reviewSections.length > 0) {
      reviewSections.forEach(section => {
        (section.paragraphs || []).forEach(p => {
          const parts = [p.content];
          parts.forEach((part, partIdx) => {
            cards.push({
              type: 'paragraph',
              id: `review-s${section.sectionId}-p${p.index}-${partIdx}`,
              content: part,
              paragraphIndex: p.index,
              sectionId: section.sectionId,
              sectionTitle: section.sectionTitle,
              reactions: p.reactions,
            });
          });
        });
      });
    } else {
      paragraphs.forEach(p => {
        const pIndex = p.index;
        const parts = [p.content];
        parts.forEach((part, partIdx) => {
          cards.push({
            type: 'paragraph',
            id: `p-${pIndex}-${partIdx}`,
            content: part,
            paragraphIndex: pIndex,
            sectionId: sectionId,
            sectionTitle: state?.sectionTitle,
            reactions: p.reactions,
          });
        });
      });
    }

    if (trailer && trailer.trim() !== '') {
      let parts: string[];
      if (onboardingPreview) {
        parts = [getOnboardingTrailerPreview(trailer)];
      } else {
        parts = splitTextByLimit(trailer, charLimit);
      }
      parts.forEach((part, idx) => {
        cards.push({
          type: 'trailer',
          id: `trailer-${idx}`,
          content: part,
        });
      });
    }

    return cards;
  }, [
    analysisParagraphs,
    getCharLimit,
    getOnboardingTrailerPreview,
    isBookAnalysis,
    onboardingPreview,
    paragraphs,
    previousSummary,
    splitTextByLimit,
    trailer,
    reviewMode,
    reviewSections,
    sectionId,
    state?.sectionTitle,
  ]);

  const totalCards = unifiedCards.length;
  const currentCard = unifiedCards[currentCardIndex];
  const firstParagraphCardIndex = useMemo(
    () => unifiedCards.findIndex(c => c.type === 'paragraph'),
    [unifiedCards]
  );
  const firstTrailerCardIndex = useMemo(
    () => unifiedCards.findIndex(c => c.type === 'trailer'),
    [unifiedCards]
  );
  const hasTrailerCard = firstTrailerCardIndex >= 0;
  const lockForwardBeforeSecondParagraph = false;

  const canShowReactionMenu =
    !onboardingPreview || guideStage >= NOVEL_GUIDE_STAGE.FIRST_PARAGRAPH_SEEN;
  const chatButtonLocked = onboardingPreview && !hasChatCharacters;
  const characterButtonLocked =
    onboardingPreview &&
    (!hasCharacterInsights ||
      (hasChatCharacters &&
        guideStageRef.current < NOVEL_GUIDE_STAGE.CHAT_RESULT));

  const handleReactionClick = useCallback(() => {
    setUiVisible(false);
    if (onboardingPreview) {
      setOnboardingReactionMenuOpen(false);
      setOnboardingReactionGiven(true);
    }
  }, [onboardingPreview]);

  const { mutate: postInteraction } = usePostParagraphInteraction();

  const handleReactionToggle = useCallback(
    (targetSectionId: number, paragraphIndex: number, reactionKey: string) => {
      if (!targetSectionId) return;
      if (onboardingPreview) {
        setOnboardingReactionMenuOpen(false);
        setOnboardingReactionGiven(true);
      }
      postInteraction({
        section_id: targetSectionId,
        paragraph_index: paragraphIndex,
        interaction_type: 'reaction',
        content: reactionKey,
        parent_interaction_id: null,
      });
    },
    [onboardingPreview, postInteraction]
  );

  const paginate = useCallback(
    (newDirection: number) => {
      if (newDirection > 0 && lockForwardBeforeSecondParagraph) {
        return;
      }
      const newIndex = currentCardIndex + newDirection;
      if (newIndex >= 0 && newIndex < totalCards) {
        const currentCard = unifiedCards[currentCardIndex];
        const nextCard = unifiedCards[newIndex];

        if (
          currentCard?.type === 'previous_summary' &&
          nextCard?.type === 'paragraph' &&
          !introSplashShown.current
        ) {
          introSplashShown.current = true;
          setSplashState('intro');
          setCurrentCardIndex(newIndex);
          return;
        }

        setCurrentCardIndex(newIndex);

        if (
          nextCard?.type === 'paragraph' &&
          currentCard?.type === 'paragraph'
        ) {
          const currentSectionId = currentCard.sectionId || sectionId;
          const nextSectionId = nextCard.sectionId || sectionId;
          const sectionChanged =
            !!currentSectionId &&
            !!nextSectionId &&
            currentSectionId !== nextSectionId;
          const notShownYet =
            !!nextSectionId &&
            !shownSectionSplashRef.current.has(nextSectionId);
          if (sectionChanged && notShownYet) {
            shownSectionSplashRef.current.add(nextSectionId);
            setSplashTitle(nextCard.sectionTitle || 'شروع بخش');
            setSplashState('intro');
          }
        }

        if (nextCard?.type === 'trailer' && !trailerSplashShown.current) {
          trailerSplashShown.current = true;
          setSplashState('trailer');
        }
      }
    },
    [
      currentCardIndex,
      totalCards,
      unifiedCards,
      lockForwardBeforeSecondParagraph,
    ]
  );

  const handleSubmit = useCallback(async () => {
    if (isBookAnalysis) {
      navigate(returnTo, {
        state: {
          bookId,
        },
      });
      return;
    }

    if (reviewMode === 'chapter_review_novel') {
      navigate('/review/result', {
        replace: true,
        state: {
          accuracy_percentage: 100,
          total_time_spent: '00:00',
          message: 'مرور رمان تکمیل شد',
          correct_answers: 0,
          total_questions: 0,
        },
      });
      return;
    }

    if (submitting || !sectionId) return;
    try {
      setSubmitting(true);
      const totalTimeSpentSeconds = Math.max(
        0,
        Math.floor((Date.now() - sessionStartTime) / 1000)
      );

      const res = await completeSectionMutation.mutateAsync({
        section_id: sectionId,
        reading_duration: totalTimeSpentSeconds,
        answers: [],
      });

      const notificationAttribution = getNotificationAttribution();
      const hasMatchingNotificationAttribution =
        (state?.source === 'notification' || notificationAttribution) &&
        (!notificationAttribution?.targetBookId ||
          notificationAttribution.targetBookId === Number(res.book_id));

      void analyticsService.logEvent('section_completed', {
        section_id: String(sectionId),
        book_id: String(res.book_id),
        book_title: res.book_title,
        book_category: res.book_category,
        reading_duration: totalTimeSpentSeconds,
        content_type: res.content_type,
        flow: onboardingPreview ? 'onboarding' : 'normal',
        ...(state?.source === 'notification' ||
        hasMatchingNotificationAttribution
          ? {
              source: 'notification',
              notification_campaign:
                state?.notificationCampaign ||
                notificationAttribution?.campaign ||
                'unknown',
              ...(notificationAttribution?.targetBookId
                ? {
                    notification_book_id: String(
                      notificationAttribution.targetBookId
                    ),
                  }
                : {}),
            }
          : {}),
      });

      if (onboardingPreview) markOnboardingSectionCompleted();

      // Invalidate all related queries after successful section completion
      // This ensures fresh data when user returns to learning path or opens bottom sheets
      if (bookId) {
        // Invalidate learning path data
        await queryClient.invalidateQueries({
          queryKey: ['my-sections', bookId],
        });

        // Invalidate timeline and character data
        await queryClient.invalidateQueries({
          queryKey: ['timeline-events', bookId],
        });
        await queryClient.invalidateQueries({
          queryKey: ['character-analysis', bookId],
        });
        await queryClient.invalidateQueries({
          queryKey: ['character-relationships', bookId],
        });

        // Invalidate library data
        await queryClient.invalidateQueries({ queryKey: ['user-library'] });
        await queryClient.invalidateQueries({
          queryKey: ['book-detail', bookId],
        });
      }

      navigate(
        onboardingPreview ? '/onboarding/xp-claim' : '/learning/xp-claim',
        {
          state: {
            book_id: bookId,
            points: res.points,
            time: res.time,
            success_percentage: res.success_percentage,
            message: res.message,
            total_questions: 0,
            streak: res.streak,
            streak_days: res.streak_days,
            previous_streak: res.previous_streak,
            previous_level: res.previous_level,
            previous_level_xp: res.previous_level_xp,
            previous_next_level_xp: res.previous_next_level_xp,
            level: res.level,
            current_level_xp: res.current_level_xp,
            next_level_xp: res.next_level_xp,
            level_up: res.level_up,
            received_gems: res.received_gems,
            received_xp: res.received_xp,
            daily_goal: res.daily_goal,
            streak_goal: res.streak_goal,
            daily_goal_gems: res.daily_goal_gems,
            daily_goal_xp: res.daily_goal_xp,
            streak_goal_gems: res.streak_goal_gems,
            streak_goal_xp: res.streak_goal_xp,
            today_minutes: res.today_minutes,
            previous_minutes: res.previous_minutes,
            initialGems,
          },
          replace: true,
        }
      );
    } catch (e) {
      console.error('Submit section failed', e);
      alert('خطا در ارسال بخش داستان');
      navigate(
        onboardingPreview ? '/onboarding/xp-claim' : '/learning/xp-claim',
        { replace: true }
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    bookId,
    completeSectionMutation,
    initialGems,
    isBookAnalysis,
    navigate,
    returnTo,
    sectionId,
    sessionStartTime,
    submitting,
    reviewMode,
    state,
  ]);

  useEffect(() => {
    if (onboardingPreview) {
      setOnboardingNovelStep('paragraph');
      setOnboardingReactionMenuOpen(true);
      setOnboardingReactionGiven(false);
    }
  }, [onboardingPreview, sectionId]);

  const continueOnboardingNovel = useCallback(() => {
    const openCharacters = () => {
      logNovelFeatureClick('character_analysis');
      setOnboardingNovelStep('characters');
      setUiVisible(false);
      setHasOpenedCharacterAnalysis(true);
      setIsCharacterAnalysisOpen(true);
      setGuideWithStage(
        NOVEL_GUIDE_STAGE.CHARACTER_OPENED,
        NOVEL_ONBOARDING_GUIDE.characterIntro
      );
    };
    const openTimeline = () => {
      logNovelFeatureClick('timeline');
      setOnboardingNovelStep('timeline');
      setUiVisible(false);
      setHasOpenedTimeline(true);
      setIsTimelineOpen(true);
    };
    const openTrailer = () => {
      setOnboardingNovelStep('trailer');
      setUiVisible(false);
      if (firstTrailerCardIndex >= 0) {
        setCurrentCardIndex(firstTrailerCardIndex);
        setSplashState('trailer');
      }
    };

    if (onboardingNovelStep === 'paragraph') {
      if (hasChatCharacters) {
        setOnboardingNovelStep('chat');
        setUiVisible(false);
        setHasOpenedChat(true);
        logNovelFeatureClick('character_chat');
        setChatOpen(true);
        setGuideWithStage(
          NOVEL_GUIDE_STAGE.TAP_DONE,
          NOVEL_ONBOARDING_GUIDE.tapDone
        );
      } else if (hasCharacterInsights) {
        openCharacters();
      } else if (hasTimeline) {
        openTimeline();
      } else if (hasTrailerCard) {
        openTrailer();
      } else {
        void handleSubmit();
      }
      return;
    }

    if (onboardingNovelStep === 'chat') {
      setChatOpen(false);
      if (hasCharacterInsights) openCharacters();
      else if (hasTimeline) openTimeline();
      else if (hasTrailerCard) openTrailer();
      else void handleSubmit();
      return;
    }

    if (onboardingNovelStep === 'characters') {
      setIsCharacterAnalysisOpen(false);
      if (hasTimeline) openTimeline();
      else if (hasTrailerCard) openTrailer();
      else void handleSubmit();
      return;
    }

    if (onboardingNovelStep === 'timeline') {
      setIsTimelineOpen(false);
      if (hasTrailerCard) openTrailer();
      else void handleSubmit();
      return;
    }

    void handleSubmit();
  }, [
    firstTrailerCardIndex,
    handleSubmit,
    hasCharacterInsights,
    hasChatCharacters,
    hasTimeline,
    hasTrailerCard,
    onboardingNovelStep,
    setGuideWithStage,
  ]);

  const advanceOnboardingNovel = useCallback(() => {
    if (!onboardingPreview) return;

    // The comment sheet is an onboarding-only layer. Close it and advance
    // in the same transition so the next onboarding sheet is mounted
    // immediately instead of relying on the closing sheet's state effect.
    if (isCommentsSheetOpen) {
      setIsCommentsSheetOpen(false);
      continueOnboardingNovel();
      return;
    }

    continueOnboardingNovel();
  }, [continueOnboardingNovel, isCommentsSheetOpen, onboardingPreview]);

  useEffect(() => {
    if (!onboardingPreview) return;
    const firstOnboardingStep: OnboardingNovelStep =
      shouldShowOnboardingParagraph
        ? 'paragraph'
        : hasChatCharacters
          ? 'chat'
          : hasCharacterInsights
            ? 'characters'
            : hasTimeline
              ? 'timeline'
              : hasTrailerCard
                ? 'trailer'
                : 'paragraph';

    guideSequenceTokenRef.current += 1;
    guideStageRef.current = NOVEL_GUIDE_STAGE.INIT;
    setGuideStage(NOVEL_GUIDE_STAGE.INIT);
    guideTextRef.current = '';
    setOnboardingNovelStep(firstOnboardingStep);
    setHighlightChatButton(false);
    setHighlightCharacterButton(!hasChatCharacters && hasCharacterInsights);
    characterHighlightConsumedRef.current = false;
    setChatHasAnswered(false);
    setUiVisible(true);
    setLastTapOnCard(false);

    if (!shouldShowOnboardingParagraph) {
      if (hasChatCharacters) {
        setHighlightChatButton(true);
        setHasOpenedChat(true);
        setChatOpen(true);
        setGuideWithStage(
          NOVEL_GUIDE_STAGE.TAP_DONE,
          NOVEL_ONBOARDING_GUIDE.tapDone
        );
      } else if (hasCharacterInsights) {
        setHighlightCharacterButton(true);
        setHasOpenedCharacterAnalysis(true);
        setIsCharacterAnalysisOpen(true);
        setGuideWithStage(
          NOVEL_GUIDE_STAGE.CHARACTER_OPENED,
          NOVEL_ONBOARDING_GUIDE.characterIntro
        );
      } else if (hasTimeline) {
        setHasOpenedTimeline(true);
        setIsTimelineOpen(true);
        setOnboardingGuide(NOVEL_ONBOARDING_GUIDE.timelineIntro);
      } else if (hasTrailerCard) {
        setGuideWithStage(
          NOVEL_GUIDE_STAGE.TRAILER_ENTERED,
          NOVEL_ONBOARDING_GUIDE.trailerIntro
        );
      }
    }
  }, [
    hasCharacterInsights,
    hasChatCharacters,
    hasTimeline,
    hasTrailerCard,
    onboardingPreview,
    sectionId,
    setGuideWithStage,
    setOnboardingGuide,
    shouldShowOnboardingParagraph,
  ]);

  useEffect(() => {
    if (!onboardingPreview || !currentCard) return;

    if (
      shouldShowOnboardingParagraph &&
      currentCardIndex === firstParagraphCardIndex &&
      guideStageRef.current < NOVEL_GUIDE_STAGE.FIRST_PARAGRAPH_SEEN
    ) {
      setGuideWithStage(
        NOVEL_GUIDE_STAGE.FIRST_PARAGRAPH_SEEN,
        NOVEL_ONBOARDING_GUIDE.firstParagraph
      );
    }

    if (
      onboardingNovelStep === 'trailer' &&
      currentCardIndex === firstTrailerCardIndex &&
      firstTrailerCardIndex >= 0 &&
      guideStageRef.current < NOVEL_GUIDE_STAGE.TRAILER_ENTERED
    ) {
      setGuideWithStage(
        NOVEL_GUIDE_STAGE.TRAILER_ENTERED,
        NOVEL_ONBOARDING_GUIDE.trailerIntro
      );
    }
  }, [
    onboardingPreview,
    currentCard,
    currentCardIndex,
    firstParagraphCardIndex,
    firstTrailerCardIndex,
    onboardingNovelStep,
    shouldShowOnboardingParagraph,
    setGuideWithStage,
  ]);

  useEffect(() => {
    if (!onboardingPreview) return;
    if (chatHasAnswered && hasCharacterInsights) {
      setHighlightChatButton(false);
      if (!characterHighlightConsumedRef.current)
        setHighlightCharacterButton(true);
      setUiVisible(true);
    }
  }, [onboardingPreview, chatHasAnswered, hasCharacterInsights]);

  useEffect(() => {
    if (onboardingPreview && isTimelineOpen) {
      setOnboardingGuide(NOVEL_ONBOARDING_GUIDE.timelineIntro);
    }
  }, [isTimelineOpen, onboardingPreview, setOnboardingGuide]);

  const fontSizeClass = onboardingCompact
    ? 'text-[15.5px] leading-[2.05rem] min-[390px]:text-[16px] min-[390px]:leading-[2.15rem]'
    : 'text-[17px] leading-[2.2rem]';

  const bgColorClasses = {
    white: 'bg-gray-50',
    sepia: 'bg-[#f4ecd8]',
    dark: 'bg-gray-900',
  };

  const cardBgClasses = {
    white: 'bg-[#6e61ff] text-white',
    sepia: 'bg-[#8B5A2B] text-[#f4ecd8]',
    dark: 'bg-[#3b3a5c] text-gray-100',
  };

  if (error) {
    return (
      <div
        className={cn(
          'min-h-screen flex items-center justify-center',
          bgColorClasses[pageBgColor]
        )}
        dir="rtl"
      >
        <div className="text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <p className="text-muted-foreground font-persian">
            خطا در بارگذاری بخش داستان
          </p>
          <p className="text-sm text-red-500 mt-2">{error}</p>
          <button
            onClick={goBack}
            className="mt-4 px-6 py-2 bg-primary-500 text-white rounded-lg font-persian"
          >
            بازگشت
          </button>
        </div>
      </div>
    );
  }

  const progressPercent =
    totalCards > 0 ? ((currentCardIndex + 1) / totalCards) * 100 : 0;
  const dragControls = useDragControls();

  const onDragEnd = useCallback(
    (e: any, { offset }: PanInfo) => {
      const swipeConfidenceThreshold = 40;
      const swipe = offset.y;

      if (swipe < -swipeConfidenceThreshold) {
        paginate(1);
      } else if (swipe > swipeConfidenceThreshold) {
        paginate(-1);
      }
    },
    [paginate]
  );

  const touchStartTimeRef = useRef<number>(0);
  const touchStartPosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const touchHandledRef = useRef<boolean>(false);
  const lastTouchTapAtRef = useRef<number>(0);
  const textScrollTouchRef = useRef<{ y: number; scrollTop: number } | null>(
    null
  );
  const toggleOnboardingReactionMenu = useCallback(() => {
    if (onboardingPreview && currentCardIndex === firstParagraphCardIndex) {
      setOnboardingReactionMenuOpen(previous => !previous);
    }
  }, [currentCardIndex, firstParagraphCardIndex, onboardingPreview]);

  const handleOnboardingTextTouchStart = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (!onboardingPreview) return;
      e.stopPropagation();
      textScrollTouchRef.current = {
        y: e.touches[0].clientY,
        scrollTop: e.currentTarget.scrollTop,
      };
    },
    [onboardingPreview]
  );

  const handleOnboardingTextTouchMove = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (!onboardingPreview || !textScrollTouchRef.current) return;
      e.stopPropagation();
      e.currentTarget.scrollTop =
        textScrollTouchRef.current.scrollTop +
        (textScrollTouchRef.current.y - e.touches[0].clientY);
    },
    [onboardingPreview]
  );

  const handleOnboardingTextTouchEnd = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (!onboardingPreview) return;
      e.stopPropagation();
      textScrollTouchRef.current = null;
    },
    [onboardingPreview]
  );

  const handleCardTextPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (onboardingPreview) return;
      // Start Framer Motion's real drag from the text itself. This preserves
      // the existing spring animation instead of waiting for touchend and
      // changing the card index abruptly.
      dragControls.start(e);
    },
    [dragControls, onboardingPreview]
  );

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartTimeRef.current = Date.now();
    touchStartPosRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
    touchHandledRef.current = false;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const touchDuration = Date.now() - touchStartTimeRef.current;
      const touchEndPos = {
        x: e.changedTouches[0].clientX,
        y: e.changedTouches[0].clientY,
      };
      const deltaX = touchEndPos.x - touchStartPosRef.current.x;
      const deltaY = touchEndPos.y - touchStartPosRef.current.y;
      const movement = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      const target = e.target as HTMLElement;
      if (
        target.closest('button') ||
        target.closest('a') ||
        target.closest('[role="button"]') ||
        target.closest('input') ||
        target.closest('textarea') ||
        target.closest('.interaction-menu-popup')
      ) {
        return;
      }

      const TAP_THRESHOLD = 15;
      const TIME_THRESHOLD = 400;

      if (touchDuration < TIME_THRESHOLD && movement < TAP_THRESHOLD) {
        const isCard = !!target.closest('.current-novel-card');
        setLastTapOnCard(isCard);
        if (
          onboardingPreview &&
          currentCardIndex === firstParagraphCardIndex &&
          guideStageRef.current <= NOVEL_GUIDE_STAGE.FIRST_PARAGRAPH_SEEN
        ) {
          if (isCard) toggleOnboardingReactionMenu();
          setHighlightChatButton(true);
          setHighlightCharacterButton(false);
          setUiVisible(prev => !prev);
        } else {
          if (isCard) toggleOnboardingReactionMenu();
          setUiVisible(prev => !prev);
        }
        lastTouchTapAtRef.current = Date.now();
        touchHandledRef.current = true;
      }
    },
    [
      onboardingPreview,
      currentCardIndex,
      firstParagraphCardIndex,
      toggleOnboardingReactionMenu,
    ]
  );

  const handleScreenClick = useCallback(
    (e: React.MouseEvent) => {
      // Mobile browsers can dispatch a synthetic click after touchend. The
      // touch handler already toggled the UI, so processing that click too
      // would toggle it back and make the reaction menu flash.
      const isSyntheticTouchClick =
        Date.now() - lastTouchTapAtRef.current < 700;
      if (touchHandledRef.current || isSyntheticTouchClick) {
        touchHandledRef.current = false;
        lastTouchTapAtRef.current = 0;
        return;
      }

      const target = e.target as HTMLElement;
      if (
        target.closest('button') ||
        target.closest('[role="button"]') ||
        target.closest('.interaction-menu-popup')
      ) {
        return;
      }

      if (isCommentsSheetOpen) return;

      const isCard = !!target.closest('.current-novel-card');
      setLastTapOnCard(isCard);
      if (isCard) toggleOnboardingReactionMenu();
      if (
        onboardingPreview &&
        currentCardIndex === firstParagraphCardIndex &&
        guideStageRef.current <= NOVEL_GUIDE_STAGE.FIRST_PARAGRAPH_SEEN
      ) {
        setHighlightChatButton(true);
        setHighlightCharacterButton(false);
        setUiVisible(prev => !prev);
        return;
      }
      setUiVisible(prev => !prev);
    },
    [
      isCommentsSheetOpen,
      onboardingPreview,
      currentCardIndex,
      firstParagraphCardIndex,
      toggleOnboardingReactionMenu,
    ]
  );

  const renderCards = () => {
    const cards = [];
    for (let i = totalCards - 1; i >= 0; i--) {
      const card = unifiedCards[i];
      const diff = i - currentCardIndex;

      if (Math.abs(diff) > 1) continue;

      let headerText = '';
      if (card.type === 'previous_summary') headerText = 'آنچه گذشت';
      if (card.type === 'trailer') headerText = 'آنچه خواهید خواند';

      const isCurrent = diff === 0;
      const opacity = isCurrent ? 1 : 0.6;
      const scale = isCurrent ? 1 : 0.8;
      const yOffset = diff * 230;
      const zIndex = 50 - Math.abs(diff);
      const filterBlur = isCurrent ? '0px' : '6px';

      const isLastCardInDeck = i === totalCards - 1;
      const isOnboardingTrailerCard =
        onboardingPreview && card.type === 'trailer';
      const hasExistingReactions =
        typeof card.reactions === 'number'
          ? card.reactions > 0
          : Array.isArray(card.reactions) && card.reactions.length > 0;
      const showOnboardingReactionMenu =
        onboardingPreview &&
        isCurrent &&
        card.type === 'paragraph' &&
        i === firstParagraphCardIndex;
      // Reserve the reaction row only when it exists or has just been added;
      // an empty reaction row must not leave unused space in the card.
      const onboardingTextReserve =
        onboardingPreview &&
        card.type === 'paragraph' &&
        (hasExistingReactions || onboardingReactionGiven)
          ? 60
          : 0;
      const cardFontSize = onboardingPreview
        ? getOnboardingNovelFontSize(
            card.content,
            onboardingTextReserve,
            card.type === 'trailer'
          )
        : getNovelFontSize(card.content, hasExistingReactions);

      cards.push(
        <motion.div
          key={card.id + i}
          className={cn(
            'interactive-card absolute rounded-2xl shadow-2xl flex flex-col',
            isOnboardingTrailerCard ? 'justify-start' : 'justify-center',
            onboardingCompact
              ? 'w-[90%] sm:w-[86%] max-w-[340px] sm:max-w-[390px] px-2.5 py-3 sm:px-3 sm:py-4'
              : 'w-[90%] sm:w-[85%] max-w-sm sm:max-w-md md:max-w-lg px-3 py-4 sm:px-4 sm:py-5',
            cardBgClasses[pageBgColor],
            isCurrent && 'current-novel-card'
          )}
          initial={{
            y: yOffset + (diff > 0 ? 50 : -50),
            opacity: 0,
            scale: scale * 0.9,
          }}
          animate={{ y: yOffset, opacity, scale }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          drag={isCurrent && !onboardingPreview ? 'y' : false}
          dragControls={dragControls}
          dragListener={false}
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={0.8}
          onDragStart={
            isCurrent
              ? () => {
                  setUiVisible(false);
                  setLastTapOnCard(false);
                }
              : undefined
          }
          onDragEnd={isCurrent ? onDragEnd : undefined}
          style={{
            zIndex,
            filter: filterBlur,
            touchAction: isCurrent && !onboardingPreview ? 'none' : 'auto',
            pointerEvents: isCurrent ? 'auto' : 'none',
            margin: '0 auto',
            // Preserve blur/scale visuals while keeping the card
            // on a stable compositor layer during a swipe.
            willChange: 'transform, opacity, filter',
            backfaceVisibility: 'hidden',
            height: onboardingCompact ? 'auto' : undefined,
            maxHeight: onboardingCompact ? 'calc(100% - 28px)' : undefined,
          }}
        >
          {isCurrent &&
            card.type === 'paragraph' &&
            (card.sectionId || sectionId) &&
            !isBookAnalysis && (
              <>
                {((showOnboardingReactionMenu &&
                  onboardingReactionMenuOpen &&
                  !isCommentsSheetOpen) ||
                  (!onboardingPreview &&
                    uiVisible &&
                    lastTapOnCard &&
                    !isCommentsSheetOpen &&
                    canShowReactionMenu)) && (
                  <ParagraphInteractionMenu
                    isVisible={true}
                    sectionId={card.sectionId || sectionId || 0}
                    paragraphIndex={card.paragraphIndex!}
                    theme={pageBgColor}
                    guideTargetId="novel-reading-reaction-menu"
                    onCommentClick={() => setIsCommentsSheetOpen(true)}
                    onReactionSelect={handleReactionClick}
                  />
                )}
              </>
            )}

          {headerText && (
            <div
              className={cn(
                'w-full border-b border-white/20',
                isOnboardingTrailerCard ? 'mb-2 pb-2' : 'mb-5 pb-3'
              )}
            >
              <h3
                className={cn(
                  'text-right font-bold font-persian opacity-95 break-words',
                  !isOnboardingTrailerCard && 'text-lg md:text-xl'
                )}
                style={
                  isOnboardingTrailerCard
                    ? {
                        fontSize: `${Math.min(17, Math.max(16, window.innerWidth * 0.045))}px`,
                        lineHeight: '1.5',
                      }
                    : undefined
                }
              >
                {headerText}
              </h3>
            </div>
          )}

          <div
            className={cn(
              'w-full min-h-0 overflow-y-auto hide-scrollbar',
              onboardingPreview ? 'max-h-full' : 'max-h-[calc(100dvh-12rem)]'
            )}
            onPointerDown={
              isCurrent && !onboardingPreview
                ? handleCardTextPointerDown
                : undefined
            }
            onTouchStart={
              onboardingPreview ? handleOnboardingTextTouchStart : undefined
            }
            onTouchMove={
              onboardingPreview ? handleOnboardingTextTouchMove : undefined
            }
            onTouchEnd={
              onboardingPreview ? handleOnboardingTextTouchEnd : undefined
            }
            onTouchCancel={
              onboardingPreview ? handleOnboardingTextTouchEnd : undefined
            }
            style={!onboardingPreview ? { touchAction: 'none' } : undefined}
          >
            <p
              className={cn(
                'w-full text-justify font-persian whitespace-pre-wrap break-words [overflow-wrap:anywhere]',
                'drop-shadow-sm [text-align-last:right]',
                !onboardingPreview && 'font-medium',
                fontSizeClass
              )}
              style={{
                fontSize: `${cardFontSize}px`,
                lineHeight: `${cardFontSize * 2.05}px`,
              }}
            >
              {card.content}
            </p>
          </div>

          {card.type === 'paragraph' &&
            (card.sectionId || sectionId) &&
            !isBookAnalysis && (
              <ParagraphCardReactions
                sectionId={card.sectionId || sectionId || 0}
                paragraphIndex={card.paragraphIndex!}
                isCurrent={isCurrent}
                onReactionToggle={(pIdx, key) =>
                  handleReactionToggle(
                    card.sectionId || sectionId || 0,
                    pIdx,
                    key
                  )
                }
                toPersianDigits={toPersianDigits}
                initialTotalReactions={
                  typeof card.reactions === 'number'
                    ? card.reactions
                    : undefined
                }
                theme={pageBgColor}
              />
            )}

          {isCurrent &&
            !onboardingPreview &&
            !isLastCardInDeck &&
            splashState === 'none' && (
              <div className="absolute -bottom-14 left-0 right-0 opacity-40 flex justify-center animate-slow-bounce pointer-events-none">
                <div
                  className={cn(
                    'p-2 rounded-full',
                    bgColorClasses[pageBgColor] === 'dark'
                      ? 'text-gray-400'
                      : 'text-gray-600'
                  )}
                >
                  <svg
                    className="w-8 h-8 opacity-70"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M19 14l-7 7m0 0l-7-7m7 7V3"
                    />
                  </svg>
                </div>
              </div>
            )}
        </motion.div>
      );
    }
    return cards;
  };

  const renderedCards = useMemo(
    () => renderCards(),
    [
      totalCards,
      unifiedCards,
      currentCardIndex,
      onboardingPreview,
      onboardingCompact,
      pageBgColor,
      uiVisible,
      lastTapOnCard,
      isCommentsSheetOpen,
      canShowReactionMenu,
      isBookAnalysis,
      splashState,
      hasChatCharacters,
      handleReactionClick,
      handleReactionToggle,
      toPersianDigits,
      onDragEnd,
      getNovelFontSize,
      getOnboardingNovelFontSize,
      onboardingReactionMenuOpen,
      onboardingReactionGiven,
    ]
  );

  const finalOnboardingStep: OnboardingNovelStep = hasTrailerCard
    ? 'trailer'
    : hasTimeline
      ? 'timeline'
      : hasCharacterInsights
        ? 'characters'
        : hasChatCharacters
          ? 'chat'
          : 'paragraph';
  const isOnboardingFinalCheckpoint =
    onboardingPreview && onboardingNovelStep === finalOnboardingStep;
  const onboardingNovelCtaLabel = isOnboardingFinalCheckpoint
    ? 'اتمام بخش'
    : 'ادامه';

  return (
    <div
      className={cn(
        'h-[100svh] w-full transition-colors duration-500 relative flex flex-col overflow-hidden fixed touch-none inset-0',
        bgColorClasses[pageBgColor]
      )}
      style={{
        overscrollBehavior: 'none',
        height: '100dvh',
        paddingTop: onboardingPreview
          ? 'var(--onboarding-header-h, 110px)'
          : undefined,
      }}
      dir="rtl"
      onClick={handleScreenClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        @keyframes slowBounce {
          0%, 100% {
            transform: translateY(-25%);
            animation-timing-function: cubic-bezier(0.8,0,1,1);
          }
          50% {
            transform: translateY(0);
            animation-timing-function: cubic-bezier(0,0,0.2,1);
          }
        }
        .animate-slow-bounce {
          animation: slowBounce 2.5s infinite;
        }
      `}</style>

      <>
        {splashState === 'intro' && (
          <SplashOverlay
            key="novel-reading-intro-splash"
            text={splashTitle}
            onComplete={handleIntroSplashComplete}
          />
        )}
        {splashState === 'previous' && (
          <SplashOverlay
            key="novel-reading-previous-splash"
            text="آنچه گذشت"
            onComplete={handlePreviousSplashComplete}
          />
        )}
        {splashState === 'trailer' && (
          <SplashOverlay
            key="novel-reading-trailer-splash"
            text="آنچه خواهید خواند"
            onComplete={handleTrailerSplashComplete}
          />
        )}
      </>

      {!onboardingPreview &&
        !isBookAnalysis &&
        splashState === 'none' &&
        showPreviousSummaryGuide &&
        user?.is_registered &&
        !user.is_guest && (
          <FeatureGuideOverlay
            steps={NOVEL_READING_FEATURE_GUIDES.previousSummary}
            onComplete={() => setShowPreviousSummaryGuide(false)}
          />
        )}

      {!onboardingPreview &&
        !isBookAnalysis &&
        splashState === 'none' &&
        showReadingOverviewGuide &&
        user?.is_registered &&
        !user.is_guest && (
          <FeatureGuideOverlay
            steps={NOVEL_READING_FEATURE_GUIDES.readingOverview}
            onComplete={() => setShowReadingOverviewGuide(false)}
          />
        )}

      {!onboardingPreview &&
        !isBookAnalysis &&
        splashState === 'none' &&
        showTrailerGuide &&
        user?.is_registered &&
        !user.is_guest && (
          <FeatureGuideOverlay
            steps={NOVEL_READING_FEATURE_GUIDES.trailer}
            onComplete={() => setShowTrailerGuide(false)}
          />
        )}

      {!onboardingPreview &&
        !isBookAnalysis &&
        splashState === 'none' &&
        user?.is_registered &&
        !user.is_guest && (
          <FeatureGuideOverlay
            steps={NOVEL_READING_FEATURE_GUIDES.interactions}
          />
        )}

      <AnimatePresence>
        {uiVisible && !onboardingPreview && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={cn('absolute left-0 right-0 z-[70] px-5')}
            style={{
              top: 'calc(0.25rem + env(safe-area-inset-top))',
              paddingTop: '0.5rem',
              paddingBottom: '0.5rem',
            }}
          >
            <div
              className={cn(
                'flex items-center gap-4 backdrop-blur-md p-3 rounded-2xl shadow-sm border',
                pageBgColor === 'dark'
                  ? 'bg-gray-900/70 border-gray-700/70'
                  : pageBgColor === 'sepia'
                    ? 'bg-[#eadfc8]/85 border-[#d1bea0]'
                    : 'bg-white/75 border-black/5'
              )}
            >
              <button
                onClick={e => {
                  e.stopPropagation();
                  goBack();
                }}
                className={cn(
                  'p-2 rounded-full transition-all active:scale-95',
                  pageBgColor === 'dark'
                    ? 'hover:bg-gray-800/50 bg-gray-800'
                    : 'hover:bg-gray-200/50 bg-white shadow-sm'
                )}
              >
                <svg
                  className={cn(
                    'w-5 h-5',
                    pageBgColor === 'dark' ? 'text-gray-200' : 'text-gray-700'
                  )}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth="3"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
              <div
                className={cn(
                  'flex-1 h-2.5 rounded-full overflow-hidden shadow-inner',
                  pageBgColor === 'dark'
                    ? 'bg-gray-800'
                    : pageBgColor === 'sepia'
                      ? 'bg-[#d8ccb8]'
                      : 'bg-gray-200'
                )}
              >
                <motion.div
                  className={cn(
                    'h-full rounded-full relative overflow-hidden',
                    pageBgColor === 'sepia'
                      ? 'bg-gradient-to-r from-[#8B5A2B] to-[#A0522D]'
                      : 'bg-gradient-to-r from-[#6e61ff] to-[#8a80ff]'
                  )}
                  initial={{ width: `${progressPercent}%` }}
                  animate={{ width: `${progressPercent}%` }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                >
                  <div
                    className="absolute inset-0 bg-white/20"
                    style={{
                      transform: 'skewX(-20deg)',
                      animation: 'slideRight 2s infinite',
                    }}
                  />
                </motion.div>
              </div>
              <span
                className={cn(
                  'text-sm font-bold font-persian min-w-[35px] text-center',
                  pageBgColor === 'dark' ? 'text-gray-200' : 'text-gray-700'
                )}
              >
                {toPersianDigits(currentCardIndex + 1)}{' '}
                <span className="text-xs opacity-60">از</span>{' '}
                {toPersianDigits(totalCards)}
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div
        className={cn(
          'flex-1 min-h-0 relative flex justify-center w-full px-5 perspective-1000',
          'items-center'
        )}
        style={{
          paddingTop: onboardingPreview
            ? '0.5rem'
            : 'calc(0.25rem + env(safe-area-inset-top))',
          paddingBottom: onboardingPreview
            ? '0.75rem'
            : 'calc(0.25rem + env(safe-area-inset-bottom))',
        }}
      >
        <MemoizedCardStack>{renderedCards}</MemoizedCardStack>
      </div>

      {onboardingPreview && splashState === 'none' && (
        <div className="relative shrink-0 z-[80] flex justify-center px-6 pt-2 pb-safe-bottom">
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              advanceOnboardingNovel();
            }}
            disabled={submitting}
            className={cn(
              'flex w-full max-w-sm items-center justify-center gap-3 rounded-2xl bg-[#6e61ff] py-3.5 text-[15px] font-bold font-persian text-white shadow-2xl transition-transform active:scale-95',
              submitting ? 'cursor-not-allowed opacity-70' : ''
            )}
          >
            {submitting ? (
              <svg
                className="h-5 w-5 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            ) : (
              onboardingNovelCtaLabel
            )}
          </button>
        </div>
      )}

      {!onboardingPreview &&
        currentCardIndex === totalCards - 1 &&
        splashState === 'none' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative z-50 flex shrink-0 justify-center px-5 pt-2 pb-[calc(1rem+env(safe-area-inset-bottom))]"
          >
            <button
              onClick={e => {
                e.stopPropagation();
                void handleSubmit();
              }}
              disabled={submitting}
              className={cn(
                'w-[72%] max-w-[280px] py-3.5 rounded-2xl text-center font-bold font-persian text-[15px] transition-all duration-300',
                'active:scale-95 shadow-xl hover:shadow-2xl flex items-center justify-center gap-3',
                submitting ? 'cursor-not-allowed opacity-70' : '',
                pageBgColor === 'dark'
                  ? 'bg-gray-800 text-white hover:bg-gray-700 border border-gray-600'
                  : pageBgColor === 'sepia'
                    ? 'bg-[#5c4a2f] text-[#f4ecd8] border border-[#4a3a25]'
                    : 'bg-[#6e61ff] text-white hover:bg-[#5a4ed9]'
              )}
            >
              {submitting ? (
                <svg
                  className="animate-spin h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : (
                <span>
                  {isBookAnalysis
                    ? 'اتمام تحلیل'
                    : reviewMode === 'chapter_review_novel'
                      ? 'اتمام مرور'
                      : 'تکمیل بخش'}
                </span>
              )}
            </button>
          </motion.div>
        )}

      {uiVisible && !onboardingPreview && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute bottom-8 left-0 right-0 z-50 flex justify-center"
        >
          <div className="relative flex items-center gap-3 pointer-events-auto">
            {(!onboardingPreview || hasChatCharacters) && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  if (chatButtonLocked) return;
                  logNovelFeatureClick('character_chat');
                  setUiVisible(false);
                  setShowSettingsMenu(false);
                  setChatOpen(true);
                }}
                disabled={chatButtonLocked}
                className={cn(
                  'w-14 h-14 rounded-full shadow-xl transition-all active:scale-90 flex items-center justify-center border',
                  pageBgColor === 'dark'
                    ? 'bg-gray-800 border-gray-700 text-gray-200'
                    : 'bg-white border-gray-100 text-gray-700',
                  chatButtonLocked && 'opacity-40 cursor-not-allowed',
                  highlightChatButton &&
                    'ring-4 ring-primary-500 ring-offset-2 ring-offset-transparent animate-pulse'
                )}
                data-guide="novel-reading-character-chat-button"
                aria-label="چت با کاراکتر"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.3}
                    d="M8 10h8M8 14h5m-9 5l1.2-3.6a8 8 0 1114.6 0L21 19l-4-1.1A8 8 0 014 19z"
                  />
                </svg>
              </button>
            )}
            {(!onboardingPreview || hasCharacterInsights) && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  if (characterButtonLocked) return;
                  logNovelFeatureClick('character_analysis');
                  setIsCharacterAnalysisOpen(true);
                  if (
                    onboardingPreview &&
                    guideStageRef.current < NOVEL_GUIDE_STAGE.CHARACTER_OPENED
                  ) {
                    characterHighlightConsumedRef.current = true;
                    setGuideWithStage(
                      NOVEL_GUIDE_STAGE.CHARACTER_OPENED,
                      NOVEL_ONBOARDING_GUIDE.characterIntro
                    );
                    setHighlightCharacterButton(false);
                  }
                }}
                disabled={characterButtonLocked}
                className={cn(
                  'w-14 h-14 rounded-full shadow-xl transition-all active:scale-90 flex items-center justify-center border',
                  pageBgColor === 'dark'
                    ? 'bg-gray-800 border-gray-700 text-gray-200'
                    : 'bg-white border-gray-100 text-gray-700',
                  characterButtonLocked && 'opacity-40 cursor-not-allowed',
                  highlightCharacterButton &&
                    'ring-4 ring-primary-500 ring-offset-2 ring-offset-transparent animate-pulse'
                )}
                data-guide="novel-reading-character-analysis-button"
                aria-label="تحلیل شخصیت‌ها"
              >
                <Users className="w-6 h-6" />
              </button>
            )}
            {(!onboardingPreview || hasTimeline) && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  logNovelFeatureClick('timeline');
                  setUiVisible(false);
                  setIsTimelineOpen(true);
                }}
                className={cn(
                  'w-14 h-14 rounded-full shadow-xl transition-all active:scale-90 flex items-center justify-center border',
                  pageBgColor === 'dark'
                    ? 'bg-gray-800 border-gray-700 text-gray-200'
                    : 'bg-white border-gray-100 text-gray-700'
                )}
                data-guide="novel-reading-timeline-button"
                aria-label="خط زمانی داستان"
              >
                <History className="w-6 h-6" />
              </button>
            )}
            {!onboardingPreview && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  setUiVisible(false);
                  setChatOpen(false);
                  setShowSettingsMenu(prev => !prev);
                }}
                className={cn(
                  'w-14 h-14 rounded-full shadow-xl transition-all active:scale-90 flex items-center justify-center text-gray-600 border',
                  pageBgColor === 'dark'
                    ? 'bg-gray-800 border-gray-700 text-gray-200'
                    : 'bg-white border-gray-100'
                )}
                aria-label="تنظیمات"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </button>
            )}
          </div>
        </motion.div>
      )}

      {hasOpenedChat && (
        <NovelCharacterChat
          open={chatOpen}
          onClose={() => setChatOpen(false)}
          bookId={bookId}
          analyticsBookTitle={state?.bookTitle}
          analyticsSectionId={sectionId}
          analyticsFlow={onboardingPreview ? 'onboarding' : 'normal'}
          canDisableSpoil={canDisableNovelSpoil}
          analyticsPageContext="novel_read"
          bgColor={pageBgColor}
          maxHeightVh={onboardingSheetMaxHeightVh}
          bottomOffset={onboardingPreview ? '3rem' : undefined}
          disableDismiss={onboardingPreview && !shouldShowOnboardingParagraph}
          onThinkingChange={() => {}}
          onAssistantResponse={() => {
            if (!onboardingPreview) return;
            setChatHasAnswered(true);
            setGuideWithStage(
              NOVEL_GUIDE_STAGE.CHAT_RESULT,
              NOVEL_ONBOARDING_GUIDE.chatAnswered
            );
          }}
          onAssistantError={kind => {
            if (!onboardingPreview) return;
            setChatHasAnswered(true);
            setGuideWithStage(
              NOVEL_GUIDE_STAGE.CHAT_RESULT,
              kind === 'insufficient_gems'
                ? NOVEL_ONBOARDING_GUIDE.needGems
                : NOVEL_ONBOARDING_GUIDE.generalError
            );
          }}
        />
      )}

      <AnimatePresence>
        {showSettingsMenu && !onboardingPreview && (
          <ReadingSettingsMenu
            bgColor={pageBgColor}
            onClose={() => setShowSettingsMenu(false)}
            onBgColorChange={setPageBgColor}
          />
        )}
      </AnimatePresence>

      {hasOpenedTimeline && (
        <TimelineBottomSheet
          isOpen={isTimelineOpen}
          onClose={() => setIsTimelineOpen(false)}
          sections={timelineSections}
          isLoading={timelineLoading}
          isError={timelineError}
          errorMessage={timelineErrorObj?.message}
          onRetry={() => refetchTimeline()}
          theme={pageBgColor}
          maxHeightVh={onboardingSheetMaxHeightVh}
          bottomOffset={onboardingPreview ? '3.5rem' : undefined}
          disableDismiss={onboardingPreview && !shouldShowOnboardingParagraph}
          isSpoil={isTimelineSpoil}
          onSpoilChange={handleTimelineSpoilChange}
          canDisableSpoil={canDisableNovelSpoil}
        />
      )}

      {hasOpenedCharacterAnalysis && (
        <CharacterAnalysisBottomSheet
          isOpen={isCharacterAnalysisOpen}
          onClose={() => setIsCharacterAnalysisOpen(false)}
          characters={mergedCharacters}
          isLoading={
            (!characterAnalysisData && characterAnalysisLoading) ||
            (!characterRelationshipsData && characterRelationshipsLoading)
          }
          isError={characterAnalysisError || characterRelationshipsError}
          errorMessage={
            characterAnalysisErrorObj?.message ||
            characterRelationshipsErrorObj?.message
          }
          onRetry={() => {
            refetchCharacterAnalysis();
            refetchCharacterRelationships();
          }}
          theme={pageBgColor}
          maxHeightVh={onboardingSheetMaxHeightVh}
          bottomOffset={onboardingPreview ? '6rem' : undefined}
          isOnboarding={onboardingPreview}
          disableDismiss={onboardingPreview && !shouldShowOnboardingParagraph}
          isAnalysisSpoil={isCharacterAnalysisSpoil}
          onAnalysisSpoilChange={handleCharacterAnalysisSpoilChange}
          isRelationshipSpoil={isCharacterRelationshipsSpoil}
          onRelationshipSpoilChange={handleCharacterRelationshipsSpoilChange}
          canDisableSpoil={canDisableNovelSpoil}
        />
      )}

      {hasOpenedComments && (
        <CommentsBottomSheet
          isOpen={isCommentsSheetOpen}
          onClose={() => setIsCommentsSheetOpen(false)}
          sectionId={
            (unifiedCards[currentCardIndex]?.type === 'paragraph'
              ? unifiedCards[currentCardIndex]?.sectionId
              : undefined) ||
            sectionId ||
            0
          }
          paragraphIndex={
            (unifiedCards[currentCardIndex]?.type === 'paragraph'
              ? unifiedCards[currentCardIndex]?.paragraphIndex
              : undefined) ?? 0
          }
          theme={pageBgColor}
          maxHeightVh={onboardingSheetMaxHeightVh}
          bottomOffset={onboardingPreview ? '3rem' : undefined}
        />
      )}
    </div>
  );
};

export default NovelReadingPage;
